import {
    computed,
    ComputedRef,
} from '@vue/composition-api';
import {
    ModalEntry,
    useI18n,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { Route } from 'vue-router';
import { RouteName } from '@src/app/router/constants/RouteName';
import { getConfig } from '@superbet-group/web.lib.config';
import { Country } from '@superbet-group/web.types';
import BonusProvider from '@core/models/bonus/BonusProvider';
import UserNotification from '@core/models/user/notifications/UserNotification';
import notificationsIndex from '@store/modules/ui/user/notifications/notificationsIndex';
import { NotificationType } from '@core/models/user/enums';
import multiStepVerificationStoreIndex from '@store/modules/ui/shared/multiStepVerification/multiStepVerificationIndex';
import {
    sendVerifyIdentityEvent,
    AnalyticsKycEvent,
} from '@accountFederation/modules/idCardVerification/analytics/idVerificationAnalytics';
import { verifyIdentityEntrypoints } from '@accountFederation/modules/idCardVerification/types';
import { getIdVerificationStoreGetters } from '@accountFederation/modules/idCardVerification/analytics/idVerificationStoreGetters';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import User from '@core/models/user/User';
import modalStore, { ModalDequeEntryParams } from '@store/modules/ui/shared/modal/modalIndex';
import { sendIdCardVerificationEventEntry } from '@accountFederation/modules/idCardVerification/analytics/idCardVerificationHub';
import {
    isKycStatusViewed,
    resetKycStatusViewed,
    setKycStatusViewed,
} from '@accountFederation/modules/idCardVerification/getid/isStatusViewed';
import {
    KycDetails,
    KycStatus,
} from '@accountFederation/modules/idCardVerification/getid/types';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { PromotionHubEntryEventLabels } from '@src/app/promotionHub/shared/types/PromotionHubEntryLabels';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useAccountMenuSections(pushRoute: ({ name }: { name: string }) => void, emit: any) {
    const {
        translate,
        translatePluralized,
        formatCurrency,
    } = useI18n();
    const { bonusWallet } = shellLocalConfig;

    const authModule = useVuexStore<AuthenticationStore>('data/authentication');
    const user = authModule.getter('user');
    const setUserData = authModule.action('setUserData');
    const status = computed(() => user.value?.kycDetails?.status || KycStatus.NotVerified);

    const notificationsStore = useVuexStore<typeof notificationsIndex>('ui/user/notifications');
    const notifications = notificationsStore.getter('notifications');

    const modalModule = useVuexStore<typeof modalStore>('ui/modal');
    const setModal = modalModule.action('setModal');

    const shouldVerifySms = computed(() => !!user?.value?.shouldVerifySms);

    const hasNewPhoneVerificationMenuItem = getAccountConfig().hasNewPhoneVerificationMenuItem;
    const hasVisitedAccountSecurity = authModule.getter('hasVisitedAccountSecurity');
    const isAccountSecurityVisible = getAccountConfig().isAccountSecuritySettingsEnabled && user.value !== null;
    const isMfaPreferenceEnabled = computed(() => !!user.value?.isMfaPreferenceEnabled);

    let userVerificationItems: ComputedRef<(MenuOption | null)[]>;

    const userVerificationNotifications = computed<UserNotification[]>(
        () => notifications.value.filter((notification) => [
            NotificationType.idCard,
            NotificationType.idCardVerificationPending,
            NotificationType.idCardVerificationApproved,
            NotificationType.completeRegistration,
        ].includes(notification.type as NotificationType)));
    const areMultipleDocEnabled = [Country.Ro, Country.Pl, Country.Rs].includes(getConfig().countrySpecific.country);

    if (!getAccountConfig().isCanonKycEnabled) {
        const multiStepVerificationStore = useVuexStore<typeof multiStepVerificationStoreIndex>('ui/multiStepVerification');
        const idVerificationPendingStatusModalCode = multiStepVerificationStore.getter('idVerificationPendingStatusModalCode');
        const multiStepIdVerificationGroup = multiStepVerificationStore.getter('multiStepIdVerificationGroup');

        userVerificationItems = computed(() => {
            return [
                userVerificationNotifications.value?.length && !user.value?.isVerifiedIdCard ? {
                    type: 'item',
                    tag: 'id-verification',
                    icon: 'human-user-id',
                    label: 'pam.idCardVerification.title',
                    subtextTitle: 'Know your user pending',
                    subtextColor: 'warning',
                    action: () => {
                        const modals = getNotificationModals(idVerificationPendingStatusModalCode.value, multiStepIdVerificationGroup.value);
                        notificationClick(
                            userVerificationNotifications.value[0].type as NotificationType,
                            modals,
                            pushRoute,
                            setModal,
                            user.value,
                        );
                        emit('hideDropdown');
                    },
                } : null,
                user?.value?.isVerifiedIdCard && areMultipleDocEnabled ? {
                    type: 'link',
                    tag: 'document-upload',
                    icon: 'data-file',
                    label: 'document upload',
                    action: () => pushRoute({ name: RouteName.Account.DocumentUpload }),
                } : null,
            ];
        });
    } else {
        const callKycDetails = async () => {
            const kycDetails = await authenticationService.getKycDetails(getAccountConfig().useKycLocalStoragePendingState ? user.value! : undefined);
            const oldStatus = user.value?.kycDetails?.isStatusPending ? 'pending' : user.value?.kycDetails?.status;
            const newStatus = kycDetails?.isStatusPending ? 'pending' : kycDetails.status;
            resetKycStatusViewed(oldStatus, newStatus);
            setUserData({
                key: 'kycDetails',
                value: kycDetails,
            });
        };

        if (user.value) {
            callKycDetails();
        }

        userVerificationItems = computed(() => {
            return [
                {
                    type: 'link',
                    tag: 'id-verification',
                    icon: 'human-user-id',
                    label: 'pam.idCardVerification.title',
                    subtextTitle: getKycSubtextTitle(user.value?.kycDetails, status.value),
                    subtextColor: getKycStatus(user.value?.kycDetails, status.value),
                    action: () => {
                        sendIdCardVerificationEventEntry();
                        if (status.value === KycStatus.Verified) {
                            setKycStatusViewed(true);
                        }
                        pushRoute({ name: RouteName.Account.IdentityVerification });
                    },
                },
                hasNewPhoneVerificationMenuItem ? {
                    type: 'item',
                    tag: 'sms-verification',
                    icon: 'technology-phone-number-verfication',
                    label: 'pam.account.validatePhone',
                    subtextTitle: shouldVerifySms.value ? 'pam.phoneNumber.NotVerified' : 'pam.phoneNumber.Verified',
                    subtextColor: shouldVerifySms.value ? 'danger' : 'success',
                    action: () => {
                        setModal({
                            code: getAccountConfig().hasUpliftedVerifyPhone ? modalCodes.sdsSmsValidation : modalCodes.newSmsValidation,
                            isClosable: false,
                            data: {
                                hasFooter: true,
                            },
                        });
                        emit('hideDropdown');
                    },
                } : null,
            ];
        });
    }
    const isUsernameDisplayed = shellLocalConfig.isUsernameDisplayed;

    const profileSectionItems = computed(() => {
        return [
            {
                type: 'link',
                tag: 'account-details',
                icon: 'human-user',
                label: 'account details',
                subtextTitle: isUsernameDisplayed ? `@${user.value?.username}` : user.value?.firstName,
                action: () => pushRoute({ name: RouteName.Account.Profile }),
            },
            ...userVerificationItems.value,
            shouldVerifySms.value && !hasNewPhoneVerificationMenuItem ? {
                type: 'item',
                tag: 'sms-verification',
                icon: 'technology-phone-number-verfication',
                label: 'pam.account.validatePhone',
                action: () => {
                    setModal({
                        code: getAccountConfig().hasUpliftedVerifyPhone ? modalCodes.sdsSmsValidation : modalCodes.newSmsValidation,
                        isClosable: false,
                        data: {
                            hasFooter: true,
                        },
                    });
                    emit('hideDropdown');
                },
            } : null,
            isAccountSecurityVisible ? {
                type: 'link',
                tag: 'account-security',
                icon: 'data-privacy',
                label: 'pam.account.accountSecurity',
                subtextTitle: isMfaPreferenceEnabled.value ? null : 'pam.account.improveYourSecurity',
                subtextColor: 'info',
                badge: !hasVisitedAccountSecurity.value ? {
                    background: 'elevation',
                    variant: 'primary',
                    styling: 'info',
                    size: 'md',
                    shape: 'pill',
                } : null,
                action: () => pushRoute({ name: RouteName.Account.AccountSecurity }),
            } : null,
            user.value?.isVip && shellLocalConfig.bonus?.vipUrl ? {
                type: 'link',
                tag: 'vip-club',
                icon: 'games-crown-alt',
                label: 'club',
                action: () => {
                    window.open(shellLocalConfig.bonus?.vipUrl, '_blank');
                },
            } : null,
        ].filter((item) => !!item) as MenuOption[];
    });

    const sportBonusBalance = authModule.getter('sportBonusBalance');
    const freeBetBonusBalance = authModule.getter('freeBetBonusBalance');
    const nsoftVirtualBonusBalance = authModule.getter('virtualsBonusBalance');
    const casinoBonusBalance = authModule.getter('casinoBonusBalance');
    const goldenRaceVirtualBonusBalance = authModule.getter('casinoVirtualsBonusBalance');
    const freeSpinsBonusBalance = authModule.getter('freeSpinsBonusBalance');
    const bingoBonusBalance = authModule.getter('bingoBonusBalance');
    const virtualsBonusBalance = computed(() => nsoftVirtualBonusBalance.value + goldenRaceVirtualBonusBalance.value);

    const bonusSectionItems = computed(() => {
        return [
            {
                type: 'link',
                tag: 'promo-hub',
                icon: 'commerce-gift',
                label: 'bonus_comms.promotion_hub_title',
                isOptionVisible: true,
                action: () => onPromotionHubClick(pushRoute, user.value?.userId),
            },
            {
                type: 'item',
                tag: 'sport-bonus',
                icon: 'sports-soccer',
                label: translate('bonus.overview.sport'),
                value: `${formatCurrency(sportBonusBalance.value)} ${translate('currencyAbbreviation')}`,
                isOptionVisible: true,
                action: () => {},
            },
            {
                type: 'item',
                tag: 'free-bet',
                icon: 'sports-soccer',
                label: translate('bonus.overview.free-bet'),
                value: `${formatCurrency(freeBetBonusBalance.value)} ${translate('currencyAbbreviation')}`,
                isOptionVisible: true,
                action: () => {},
            },
            {
                type: 'item',
                tag: 'casino',
                icon: 'games-casino-coin',
                label: translate('bonus.overview.casino'),
                value: `${formatCurrency(casinoBonusBalance.value)} ${translate('currencyAbbreviation')}`,
                isOptionVisible: true,
                action: () => {},
            },
            {
                type: 'item',
                tag: 'free-spins',
                icon: 'games-casino-coin',
                label: bonusWallet.awardFreeSpinsByCount ? translate('bonus.overview.free-spins') : translate('bonus.wallet.free_spin_winnings'),
                value: `${freeSpinsBonusBalance.value} ${bonusWallet.awardFreeSpinsByCount ? translatePluralized('bonus.spinsPluralized', freeSpinsBonusBalance.value) : translate('currencyAbbreviation')}`,
                isOptionVisible: getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Betler,
                action: () => {},
            },
            {
                type: 'item',
                tag: 'virtual',
                icon: 'games-horse',
                label: translate('bonus.overview.virtual'),
                value: `${formatCurrency(virtualsBonusBalance.value)} ${translate('currencyAbbreviation')}`,
                isOptionVisible: true,
                action: () => {},
            },
            {
                type: 'item',
                tag: 'live-casino',
                icon: 'games-live-casino',
                label: translate('bonus.overview.live_dealer'),
                value: `${formatCurrency(casinoBonusBalance.value)} ${translate('currencyAbbreviation')}`,
                action: () => {},
            },
            {
                type: 'item',
                tag: 'bingo',
                icon: 'games-bingo',
                label: translate('bonus.overview.bingo'),
                value: `${bingoBonusBalance.value} ${translatePluralized('bonus.ticketsPluralized', bingoBonusBalance.value)}`,
                isOptionVisible: getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Betler,
                action: () => {},
            },
        ].filter((item) => item.isOptionVisible && (!item.value || parseFloat(item.value) > 0)) as MenuOption[];
    });

    return {
        profileSectionItems,
        bonusSectionItems,
    };
}

function getNotificationModals(
    idVerificationPendingStatusModalCode: string,
    multiStepIdVerificationGroup: string | null,
): Record<Exclude<NotificationType, 'lowBalance'>, Omit<ModalEntry, 'component'>> {
    return {
        [NotificationType.sms]: {
            code: getAccountConfig().hasUpliftedVerifyPhone ? modalCodes.sdsSmsValidation : modalCodes.newSmsValidation,
        },
        [NotificationType.idCard]: {
            code: getAccountConfig().hasGetIdKyc ? modalCodes.idCardVerificationIntroGetId : modalCodes.idCardVerificationIntro,
            group: multiStepIdVerificationGroup!,
        },
        [NotificationType.idCardVerificationPending]: {
            code: getAccountConfig().hasGetIdKyc && getAccountConfig().isGetIdPendingStateUsed
                ? modalCodes.getIdVerificationPending
                : idVerificationPendingStatusModalCode,
            group: multiStepIdVerificationGroup!,
        },
        [NotificationType.idCardVerificationApproved]: {
            code: getAccountConfig().hasGetIdKyc ? modalCodes.idCardVerificationApprovedGetId : modalCodes.idCardVerificationApproved,
        },
        [NotificationType.completeRegistration]: {
            code: modalCodes.idCardVerificationIntro,
            group: multiStepIdVerificationGroup!,
        },
    };
}

function notificationClick(
    type: NotificationType,
    modals: Record<Exclude<NotificationType, 'lowBalance'>, Omit<ModalEntry, 'component'>>,
    pushRoute: ({ name }: { name: string }) => void,
    setModal: (payload: string | ModalDequeEntryParams) => Promise<void>,
    user: Nullable<User>,
) {
    switch (type) {
        case NotificationType.lowBalance:
            pushRoute({ name: RouteName.Account.Deposit });
            break;
        case NotificationType.idCard:
        case NotificationType.idCardVerificationPending:
        case NotificationType.idCardVerificationApproved:
        case NotificationType.completeRegistration:
            sendVerifyIdentityEvent({
                eventName: AnalyticsKycEvent.VerifyIdentityEntry,
                source: user?.phone
                    ? verifyIdentityEntrypoints.Notification
                    : verifyIdentityEntrypoints.NotificationCompleteRegistration,
                idVerificationStoreGetters: getIdVerificationStoreGetters(),
            });
        // eslint-disable-next-line no-fallthrough
        case NotificationType.sms:
            setModal({
                ...modals[type],
                ...(type === NotificationType.sms && { isClosable: false }),
            });
            break;
        default:
            throw new Error('Wrong notification type!');
    }
}

function onPromotionHubClick(pushRoute: ({ name }: { name: string }) => void, userId?: number) {
    AnalyticsSDK.getInstance().log(
        'Promo_Hub',
        {
            eventAction: 'Entry',
            eventLabel: PromotionHubEntryEventLabels.accountPage,
            userId: userId?.toString(),
        },
    );
    pushRoute({ name: RouteName.BonusAndComms.PromotionHub });
}

function getKycSubtextTitle(kycDetails: KycDetails | undefined, status: KycStatus): string {
    if (status === KycStatus.Verified) {
        if (!isKycStatusViewed()) {
            return `pam.notification.status.${status}`;
        }
        return 'pam.notification.uploadAdditionalDocuments';
    }

    return kycDetails?.isStatusPending ? 'pam.notification.status.pending' : `pam.notification.status.${status}`;
}

function getKycStatus(kycDetails: KycDetails | undefined, status: KycStatus): 'success' | 'info' | 'warning' | 'danger' | undefined {
    if (status === KycStatus.Verified && isKycStatusViewed()) {
        return 'success';
    } else if (status === KycStatus.Failed) {
        return 'danger';
    } else if (status === KycStatus.Incomplete) {
        return 'warning';
    }

    return kycDetails?.isStatusPending ? 'info' : 'danger';
}

export interface MenuOption {
    icon: string;
    label: string;
    tag?: string;
    subtextTitle?: string;
    subtextColor?: string | boolean;
    value?: string;
    isOptionVisible?: boolean;
    action: () => (void | Promise<Route>);
}
